import React, { ReactNode } from 'react';
import { ProductCategoryKeys, ProductVariantType } from '../../../common/enums/common';
import { IColorVariant, IProductVariant } from '../../../services/helper/productInterfaces';
import { useProductTags } from './hooks/useProductTags';
import { useProductTile } from './hooks/useProductTile';
import ProductNameAndPrice from './productNameAndPrice';
import ProductTags from './productTags';
import ProductVariants from './productVariants';
import { useTranslator } from '../header/locales-provider';

export default function ProductTile({
  productId,
  productVariants,
  showProductVariants,
  onTileClick,
  onFavoriteClick,
  onVariantChange,
  variantType,
  children,
  colorScheme,
  className,
  productTileId,
  imageClassName,
  tagClassName,
  productType,
  productAttrClassName,
  imgClassName,
  pageName,
}: Readonly<{
  productId: string;
  productVariants: IProductVariant[];
  showProductVariants: boolean;
  onTileClick?: (variant: IProductVariant, productId: string) => void;
  onFavoriteClick?: (selItem: string, isSelected: boolean) => void;
  onVariantChange?: (variant: IColorVariant) => void;
  variantType: ProductVariantType;
  colorScheme: Record<string, Record<string, string>>;
  children?: ReactNode;
  className?: string;
  productTileId: string;
  tagClassName?: string;
  imageClassName?: string;
  productType?: ProductCategoryKeys;
  productAttrClassName?: string;
  imgClassName?: string;
  pageName?: string;
}>): JSX.Element {
  const { onProductTileChange, productVariant, getSortedByReleaseDate } = useProductTile(
    productVariants,
    pageName,
  );
  const { getProductTags } = useProductTags(productVariants, productType);
  const tagItems = getProductTags(productVariant);
  const sortedVariants = getSortedByReleaseDate();
  const locales = useTranslator('global');
  const viewDetailsText = locales?.['aria-label.view.details.for'] || '';

  const onProductVariantChange = (colorVariant: IColorVariant) => {
    onProductTileChange(colorVariant);
    if (onVariantChange) {
      onVariantChange(colorVariant);
    }
  };
  const onProductTileClick = (evt: any) => {
    evt.stopPropagation();
    if (onTileClick) {
      onTileClick(productVariant, productId);
    }
  };

  const onFavoriteItemClick = (selItem: string, isSelected: boolean) => {
    if (onFavoriteClick) {
      onFavoriteClick(selItem, isSelected);
    }
  };

  const handleKeyDown = (evt: any) => {
    if (evt.key === 'Enter') {
      onProductTileClick(evt);
      evt.preventDefault();
    }
  };

  if (!productVariant) {
    return <></>;
  }

  return (
    <div
      data-testid={productId}
     // tabIndex={0}
      onKeyDown={evt => handleKeyDown(evt)}
      className={`group rounded-md border border-[#878F9B] bg-white hover:cursor-pointer  ${className}`}
      onClick={(evt: any) => onProductTileClick(evt)}
    >
      <div className="flex flex-col md:gap-3 xl:gap-4 items-center md:justify-between overflow-hidden text-center h-full">
        {/* Product Tag - Include Tags and Favorite */}
        <ProductTags
          product={productVariant}
          className={tagClassName}
          productTags={tagItems}
          productType={productType}
          onFavoriteItemClick={onFavoriteItemClick}
        />

        {/* Product Image */}
        <div className={`flex justify-center items-center ${imageClassName}`}>
          <img
            src={productVariant.media?.[0]?.src}
            role="link"
            tabIndex={0}
            aria-label={`${viewDetailsText} ${productVariant?.brand ?? ''} ${productVariant?.nameFriendly ?? productVariant?.name ?? ''}`}
            alt={`${productVariant?.brand ?? ''} ${productVariant?.nameFriendly ?? productVariant?.name ?? ''}`}
            className={`h-full object-contain transform transition-transform duration-500 overflow-clip ${imgClassName}`}
          />
        </div>

        <div className={`flex flex-col justify-between gap-1 w-full ${productAttrClassName}`}>
          {/* Product Price */}
          <ProductNameAndPrice product={productVariant} />

          {children}

          {/*  Product Colors*/}
          {/* TODO : In future Based on Variants change the handlers */}
          {showProductVariants && (
            <ProductVariants
              className="flex content-center justify-center md:h-[28px]"
              variants={getColorVariants(sortedVariants)}
              onSelectVariant={onProductVariantChange}
              variantType={variantType}
              colorScheme={colorScheme}
              pageName={pageName}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const getColorVariants = (productVariants: IProductVariant[]): IColorVariant[] => {
  return productVariants?.map((variant: IProductVariant) => {
    return {
      id: variant.id,
      sku: variant.sku,
      color: variant.color,
      releaseDate: variant.releaseDate,
    };
  });
};
